import {
  type AnalyticsResultSummary,
  type DrySessionSourceRow,
} from "../../../../models/audit/procedureSessionTableShell";

/**
 * What a (sample, analytics column) cell holds, counted across both channels the
 * row's parse fills: the verdicts it read and the columns it names for verdicts
 * it discarded.
 *
 * - `none` — no verdict of either kind stands against the cell.
 * - `verdict` — exactly one did, and it parsed.
 * - `unreadable` — the cell holds a verdict but no single readable one: its one
 *   verdict was discarded, or more than one stands against it. A parsed verdict
 *   beside a discarded one is unreadable too, because the readable one cannot be
 *   shown to be the live one; answering with it would rank verdicts by which
 *   survived the parse.
 *
 * Verdicts are not ranked, because supersession rather than recency names the
 * live one, and the store admits at most one live verdict per cell. A second one
 * means that guarantee broke in transit, and any ranking that resolved it would,
 * in the arrangement supersession exists to create, put a re-judgment written
 * already superseded above the human override it defers to. Attribute-test
 * results are ranked instead because they keep their history and nothing marks
 * one of them live.
 *
 * This classifies and does not report: reporting the collision is the workpaper
 * cell's, and this answers only what the cell reads as.
 *
 * The same rule the workpaper cell withholds its badge by, so a summary surface
 * never reports a sample untested, or passed, where the cell says it cannot tell.
 */
export type LiveAnalyticsVerdict =
  | { kind: "none" }
  | { kind: "verdict"; summary: AnalyticsResultSummary }
  | { kind: "unreadable" };

export function resolveLiveAnalyticsVerdict(
  dryRow: DrySessionSourceRow | undefined,
  analyticsColumnId: string,
): LiveAnalyticsVerdict {
  const parsed = (dryRow?.analyticsResultsSummary ?? []).filter(
    (summary) => summary.analyticsColumnId === analyticsColumnId,
  );
  // Counted rather than tested for membership: the parse names the column once
  // per verdict it discards.
  const droppedCount = (
    dryRow?.analyticsVerdictsDroppedForColumns ?? []
  ).filter((droppedForColumn) => droppedForColumn === analyticsColumnId).length;

  if (parsed.length + droppedCount > 1 || droppedCount === 1) {
    return { kind: "unreadable" };
  }
  const [summary] = parsed;
  return summary ? { kind: "verdict", summary } : { kind: "none" };
}
